import { useQuery } from '@apollo/client';
import { Typography } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import * as React from 'react';
import { useMemo, useState } from 'react';

import SimpleSearch from '@/components/simple-search';
import { stopCauseMapping } from '@/graphql/queries';
import * as Types from '@/types';
import { SensorSettingsState } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';
import MappingTable from '@/views/dialogs/sensor-settings/tabs/stop-mapping/mapping-table';

import { TabPanelComponent, TabPropsType } from '..';

export type StopCauseMapping = {
  stopCauseName?: string;
  stopCauseId: string;
  mappingTag: string;
  mappingValue: string;
  regPriority: number;
  lookBack: number;
  lookForward: number;
  split: boolean;
};

const isShown = (state: SensorSettingsState): boolean => {
  return (
    state.peripheralType === 'SENSOR' &&
    !!state.config?.sensor?.type &&
    (state.config?.sensor.type.startsWith('COUNTER') || state.config?.sensor.type === 'DISCRETE')
  );
};

export const TabProps: TabPropsType = {
  isShown,
  label: (t) => t('device:stopsMapping', { defaultValue: 'Stops mapping' }),
};

export const StopMappingTab: TabPanelComponent<{ deviceOwner?: string; peripheralId: string }> = ({
  invalidFormMessage,
  classes,
  t,
  peripheralId,
  deviceOwner: deviceOwnerProp,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [uuid, ...indexParts] = peripheralId.split('-');
  const index = indexParts.join('-');
  const header = (
    <>
      <Typography variant="body1">
        {t(['device:plcMappingText'], {
          defaultValue: 'Create unique mappings between PLC messages and stop causes',
        })}
      </Typography>
      <Typography variant="caption" display="block" style={{ marginTop: '4px', marginBottom: '16px', maxWidth: '75%' }}>
        {t(['device:dragMouserOverToSeeExplanations'], {
          defaultValue:
            'Drag your mouse over the headers to see explanation. Clicking the + icon will immediately save the mapping, and clicking the delete icon will immediately delete the mapping.',
        })}
      </Typography>
    </>
  );

  const translatedValue = t(['sensor:stopsMappingsSearch'], { defaultValue: 'Search by tag, value and stop cause' });

  const searchField = <SimpleSearch setSearch={setSearchValue} placeholder={translatedValue} />;

  const filterStopMappings = (searchValue: string, stopCauseMappings: StopCauseMapping[]) => {
    return stopCauseMappings.filter((row) => {
      return (
        row.stopCauseName?.toLowerCase().includes(searchValue) ||
        row.mappingTag.toLowerCase().includes(searchValue) ||
        row.mappingValue.toLowerCase().includes(searchValue)
      );
    });
  };

  const { refetch, data } = useQuery<Schema.StopCauseMappingQuery, Schema.StopCauseMappingQueryVariables>(
    stopCauseMapping,
    {
      ssr: false,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: {
        uuid,
        index,
      },
    },
  );

  const deviceOwner = data?.device?.owner ?? deviceOwnerProp;
  const stopCauseMappings = (data?.device?.sensor?.stopCauseMappings ?? []).filter(Types.isTruthy);
  const stopCauseCategories = (data?.device?.sensor?.stopCauseCategories ?? []).filter(Types.isTruthy);

  const stopCauses = createStopCauses(stopCauseCategories);

  const stopMappings = stopCauseMappings.map((mapping): StopCauseMapping => {
    const stopCauseName = stopCauses.find((stopCause) => stopCause.id === mapping.stopCauseId) || {
      name: t(['device:deletedStopCause'], { defaultValue: 'Deleted Stop Cause ' }),
    };

    return {
      stopCauseId: mapping.stopCauseId,
      stopCauseName: stopCauseName.name,
      mappingTag: mapping.peripheralIdEvent,
      mappingValue: mapping.value,
      regPriority: mapping.regPriority || 1,
      lookForward: mapping.lookForward,
      lookBack: mapping.lookBack,
      split: mapping.split || false,
    };
  });

  const filteredMaps = useMemo(() => filterStopMappings(searchValue, stopMappings), [searchValue, stopMappings]);

  return (
    <div className={classes.tabPanel}>
      {header}
      {searchField}
      <MappingTable
        peripheralId={peripheralId}
        stopMappings={filteredMaps}
        stopCauses={stopCauses}
        refetchQuery={refetch}
        deviceOwner={deviceOwner}
      />
      {invalidFormMessage}
    </div>
  );
};

const createStopCauses = (
  stopCauseCategories: { name: string; stopCauses: (Pick<Schema.StopCause, 'id' | 'name'> | null)[] }[],
) => {
  const stopCauses: Array<{
    name: string;
    id: string;
  }> = [];
  stopCauseCategories.map((category) =>
    category.stopCauses.filter(Types.isTruthy).map((cause) => {
      stopCauses.push({
        name: `${cause.name} - ${category.name}`,
        id: cause.id,
      });
    }),
  );
  return stopCauses;
};
